/**
 * Interactive Reading content type schema — EAA-1755.
 *
 * A single reading script that is synthesised to speech (TTS) by the AI agent.
 * The learner listens to the model audio with words highlighted in sync
 * ("Nghe đọc mẫu"); when auto-grading is enabled, the learner can also record
 * themselves reading it back ("Luyện tập") and receive a pronunciation score
 * with per-word colour feedback.
 */

import type { ContentConfig, ScoringConfig } from '@ai-content-hub/content-core';

/** The maximum script length for a reading. */
export const MAX_READING_TEXT_LENGTH = 500;

/** Default QT_* code used when proxying to the /speaking/grade scoring API. */
export const DEFAULT_READING_GRADING_QUESTION_TYPE = 'QT_SPEAK_READ_ALOUD';

/** One word boundary in the synthesised audio, in seconds from the start. */
export interface ReadingWordTimestamp {
    word: string;
    /** Normalised key for the word (agent-provided), e.g. "hello". */
    slug: string;
    start: number;
    end: number;
}

/**
 * The generated TTS stimulus. Present once the author has generated audio for
 * the current script; cleared when the script changes so stale audio is never
 * shown to the learner.
 */
export interface ReadingStimulus {
    /** Public audio URL returned by the agent (S3/CloudFront). */
    audioUrl: string;
    audioFormat?: string;
    mimeType?: string;
    /** Transcript the agent recognised (usually equals the script). */
    transcript?: string;
    /** Per-word timing used to highlight words during playback. */
    wordTimestamps: ReadingWordTimestamp[];
    /** Language the audio was generated for. */
    language?: string;
    /** The exact script the audio was generated from (drives staleness checks). */
    generatedFrom?: string;
}

export interface InteractiveReadingData {
    /** Reading title (shown above the passage). */
    title?: string;
    /** Instructions for the learner. */
    description?: string;
    /** The reading script (<= MAX_READING_TEXT_LENGTH chars). */
    text: string;
    /** TTS language. Defaults to `en`. */
    language: string;
    /** 0-based Gemini TTS voice index used to generate the audio. Undefined → agent default (Kore). */
    voice?: number;
    /**
     * When `true`, the activity is a pronunciation assessment: the learner may
     * switch to record mode and is auto-graded. When `false`, it is pure
     * reading/listening material (listen only).
     */
    autoGrade: boolean;
    /** Generated TTS audio + word timing. Undefined until the author generates it. */
    stimulus?: ReadingStimulus;
}

/** The learner's stored answer/attempt. */
export interface InteractiveReadingAnswer {
    /** The learner's recording as a data URL (record mode). */
    recordingDataUrl?: string;
    /** Overall pronunciation score 0-100 from the last graded attempt. */
    score?: number;
    /** Raw scoring API result (kept for per-word colour feedback). */
    scoreResult?: Record<string, unknown>;
}

export interface InteractiveReadingScoringConfig extends ScoringConfig {
    /** Passing threshold (0-1). */
    passingThreshold?: number;
    /** QT_SPEAK_* code sent to the /speaking/grade proxy. */
    gradingQuestionType?: string;
    /** Learner id fallback for grading (prefer ContentPlayer userId / embed ?userId=). */
    userId?: string;
}

export interface InteractiveReadingConfig extends Omit<ContentConfig, 'scoring'> {
    scoring?: InteractiveReadingScoringConfig;
}

export interface InteractiveReadingContent {
    id: string;
    type: 'interactive-reading';
    version: string;
    data: InteractiveReadingData;
    config?: InteractiveReadingConfig;
}

export const defaultInteractiveReadingI18n = {
    en: {
        // Editor
        contentTab: 'Content',
        title: 'Title',
        enterTitle: 'Enter the reading title',
        instructionsOptional: 'Instructions (Optional)',
        enterInstructions: 'Listen and read along with the passage below.',
        textLabel: 'Text content (Text to Speech)',
        inputModeText: 'Enter text',
        inputModeFile: 'Upload file (.txt, .docx)',
        textPlaceholder: 'Type or paste the text to be read aloud by Text to Speech…',
        textHelper:
            'This text is read by a synthesised voice and is also the reference used to score pronunciation when Auto-Grading is on.',
        charCount: 'characters',
        charLimitReached: 'Maximum length reached',
        voiceLabel: 'Voice',
        voiceLoading: 'Loading voices…',
        autoGradeLabel: 'Auto-Grading',
        autoGradeHelper:
            'When on, learners can switch to record mode, read the passage back, and get an automatic pronunciation score.',
        language: 'Voice language',
        generateAudio: 'Generate audio',
        regenerateAudio: 'Regenerate audio',
        generating: 'Generating audio…',
        audioReady: 'Audio ready',
        wordsTimed: 'words timed',
        stimulusStale: 'The text changed — regenerate the audio to update the model reading.',
        generateError: 'Could not generate audio. Please try again.',
        recordError: 'Could not access the microphone. Please check permissions and try again.',
        scoreError: 'Could not score your reading. Please try again.',
        uploadHint: 'Choose a .txt or .docx file to extract the reading text.',
        removeFile: 'Remove file',
        preview: 'Model reading preview',
        // Player
        listenTab: 'Listen to sample',
        practiceTab: 'Practice',
        play: 'Play',
        pause: 'Pause',
        record: 'Record',
        stop: 'Stop',
        submit: 'Submit',
        retry: 'Try again',
        scoring: 'Scoring your pronunciation…',
        yourScore: 'Your score',
        wordScore: 'Score',
        noAudioYet: 'The model audio has not been generated for this reading yet.',
        recordAgain: 'Record again',
        listenFirst: 'Listen to the model reading first, then practise.',
        speed: 'Speed',
    },
    vi: {
        // Editor
        contentTab: 'Nội dung',
        title: 'Tiêu đề',
        enterTitle: 'Nhập tiêu đề bài đọc',
        instructionsOptional: 'Hướng dẫn bài tập (Optional)',
        enterInstructions: 'Nghe và đọc theo đoạn văn bên dưới.',
        textLabel: 'Nội dung văn bản (Text to Speech)',
        inputModeText: 'Nhập nội dung',
        inputModeFile: 'Tải file (.txt, .docx)',
        textPlaceholder: 'Nhập hoặc dán văn bản sẽ được đọc bằng công nghệ Text to Speech…',
        textHelper:
            'Văn bản này sẽ được đọc bằng giọng nói tổng hợp, và cũng là nội dung dùng để chấm điểm phát âm nếu bật Tự động chấm bên dưới.',
        charCount: 'ký tự',
        charLimitReached: 'Đã đạt giới hạn ký tự',
        voiceLabel: 'Giọng đọc',
        voiceLoading: 'Đang tải giọng đọc…',
        autoGradeLabel: 'Tự động chấm (Auto-Grading)',
        autoGradeHelper:
            'Khi bật, học viên có thể chuyển sang chế độ ghi âm đọc lại đoạn văn và sẽ được hệ thống tự động chấm điểm phát âm.',
        language: 'Ngôn ngữ giọng đọc',
        generateAudio: 'Tạo giọng đọc',
        regenerateAudio: 'Tạo lại giọng đọc',
        generating: 'Đang tạo giọng đọc…',
        audioReady: 'Đã có giọng đọc',
        wordsTimed: 'từ đã đồng bộ',
        stimulusStale: 'Văn bản đã thay đổi — hãy tạo lại giọng đọc để cập nhật bản đọc mẫu.',
        generateError: 'Không thể tạo giọng đọc. Vui lòng thử lại.',
        recordError: 'Không thể truy cập micro. Vui lòng kiểm tra quyền và thử lại.',
        scoreError: 'Không thể chấm điểm bài đọc. Vui lòng thử lại.',
        uploadHint: 'Chọn tệp .txt hoặc .docx để trích xuất nội dung bài đọc.',
        removeFile: 'Xóa tệp',
        preview: 'Nghe thử bản đọc mẫu',
        // Player
        listenTab: 'Nghe đọc mẫu',
        practiceTab: 'Luyện tập',
        play: 'Phát',
        pause: 'Tạm dừng',
        record: 'Ghi âm',
        stop: 'Dừng',
        submit: 'Nộp bài',
        retry: 'Thử lại',
        scoring: 'Đang chấm điểm phát âm…',
        yourScore: 'Điểm của bạn',
        wordScore: 'Điểm',
        noAudioYet: 'Bài đọc này chưa được tạo giọng đọc mẫu.',
        recordAgain: 'Ghi âm lại',
        listenFirst: 'Hãy nghe bản đọc mẫu trước, sau đó luyện tập.',
        speed: 'Tốc độ',
    },
};

export const defaultInteractiveReadingConfig: InteractiveReadingConfig = {
    behavior: {
        allowRetry: true,
        showCorrectAnswer: false,
        showExplanation: false,
    },
    scoring: {
        passingThreshold: 0.7,
        gradingQuestionType: DEFAULT_READING_GRADING_QUESTION_TYPE,
    },
    display: {
        showTitle: false,
        showDescription: false,
    },
    i18n: {
        defaultLanguage: 'en',
        languages: defaultInteractiveReadingI18n,
    },
};

export const defaultInteractiveReadingData: InteractiveReadingData = {
    title: '',
    description: 'Listen and read along with the passage below.',
    text: '',
    language: 'en',
    autoGrade: false,
};

export const interactiveReadingSchema = {
    type: 'interactive-reading' as const,
    title: 'Interactive Reading',
    description: 'TTS reading with synced word highlighting and optional pronunciation grading',
    version: '1.0.0',
    category: 'assessment' as const,
    icon: 'AudioLines',
    tags: ['reading', 'speaking', 'audio', 'tts', 'pronunciation', 'assessment'],
    defaultData: defaultInteractiveReadingData,
    defaultConfig: defaultInteractiveReadingConfig,
    fields: [],
};
